// dopple-init.js - V2 Dopple SDK early initialization
// =============================================================================
// Loaded as a type="module" from <head> on V2 so the browser can start fetching
// and initializing the Dopple SDK as early as possible. UI glue code and
// window.updateSelection / window.captureCartSnapshot / etc. live in
// the-anthros-chair-2026.js, which executes after this module.
// =============================================================================

import { DoppleXR } from 'https://builds.dopple.io/packages/dopple-sdk@2026.21.0/dopple-sdk.js'

const currentPath = window.location.pathname
const isMobile = window.innerWidth <= 900

// Configuration menu mapping for Dopple
const configMenu = [
    {
        title: 'Chair Color Options',
        property: 'chassisColor',
        options: [
            { title: 'ONYX', value: 'onyx' },
            { title: 'QUARTZ', value: 'quartz' },
        ]
    },
    {
        title: 'Fabric Options',
        property: 'fabric',
        options: [
            { title: 'IVORY', value: 'ivoryGAFabric' },
            { title: 'ONYX', value: 'heatherBlackGAFabric' },
            { title: 'STEEL', value: 'steelGAFabric' },
            { title: 'DUNE', value: 'duneGAFabric' },
            { title: 'PINK', value: 'pinkGAFabric' },
            { title: 'LAVENDER', value: 'lavenderGAFabric' },
            { title: 'IVORY PORT', value: 'ivoryPort' },
            { title: 'ONYX PORT', value: 'onyxPort' },
            { title: 'STEEL PORT', value: 'steelPort' },
            { title: 'DUNE PORT', value: 'dunePort' },
        ]
    },
    {
        title: 'Back Panel Options',
        property: 'backPanel',
        options: [
            { title: 'ONYX', value: 'standardBlackV2' },
            { title: 'IVORY', value: 'standardIvory' },
            { title: 'NATURAL OAK +$50', value: 'naturalOakV2' },
            { title: 'AMBER ELM +$50', value: 'sierraOakV2' },
            { title: 'WARM EBONY +$50', value: 'warmEbony' },
            { title: 'IVORY ASH +$50', value: 'ivoryOakV2' },
            { title: 'GREIGE OAK +$50', value: 'greyOakV2' },
            { title: 'WALNUT +$94', value: 'walnutV2' },
            { title: 'CARBON FIBER +$188', value: 'carbonFushion' },
            { title: 'CIRCUIT BLACK +$50', value: 'circuitOnyx' },
            { title: 'CIRCUIT WHITE +$50', value: 'circuitIvory' },
            { title: 'SKELETON WHITE +$50', value: 'skeleton1' },
            { title: 'SKELETON BLACK +$50', value: 'skeleton2' },
            { title: 'BITCOIN MONEY +$50', value: 'bitcoinMoney' },
            { title: 'BITCOIN LETTERPRESS +$50', value: 'bitcoinLetterpress' },
            { title: 'US FLAG +$50', value: 'colorFlagUSA' },
        ]
    },
    {
        title: 'Control Arm Cap Color',
        property: 'controlArmCapColor',
        options: [
            { title: 'GLOSS STONE', value: 'glossStone' },
            { title: 'SHINY BLACK', value: 'shinyBlack' },
            { title: 'SHINY CHROME', value: 'shinyChrome' },
            { title: 'SHINY GOLD', value: 'shinyGold' },
        ]
    },
    {
        title: 'Base Style Options',
        property: 'base',
        options: [
            { title: '4 SPOKE BASE', value: 'fourSpoke' },
            { title: '5 SPOKE BASE', value: 'fiveSpoke' },
        ]
    },
    {
        title: 'Arm Options',
        property: '4DArms',
        options: [
            { title: 'NO ARMS', value: 'none' },
            { title: '4D ARMS +$62', value: 'add' },
        ]
    }
];

window.configMenu = configMenu;

// Default selection
const selection = {
    '4DArms': 'add',
    'autoRotation': isMobile ? 'off' : 'on',
    'backPanel': 'standardBlack',
    'base': 'fiveSpoke',
    'cameraView': 'front',
    'chassisColor': 'onyx',
    'controlArmCapColor': 'shinyBlack',
    'fabric': 'heatherBlackGAFabric',
    'hotspots': 'off',
}

// URL-based preset configurations
if (currentPath === '/products/anthros-chair-configuration-one') {
    Object.assign(selection, {
        'fabric': 'heatherBlackGAFabric',
        'backPanel': 'walnut'
    })
} else if (currentPath === '/products/anthros-chair-configuration-two') {
    Object.assign(selection, {
        'fabric': 'ivoryGAFabric',
        'backPanel': 'naturalOakV2',
        'chassisColor': 'quartz',
        'controlArmCapColor': 'glossStone'
    })
}

(async () => {
  // Initialize Dopple
    const dopple = new DoppleXR({
        container: document.querySelector("#dopple-container"),
        selection,
        productVersion: "26",
        owner: "anthros",
        workspace: "anthros",
        projectName: "anthros-the-chair",
    })
  window.dopple = dopple

  // Progress tracking
  const progressElement = document.querySelector(".progress");
  if (progressElement) {
      dopple.loadingManager.onProgress = (_url, current, total) => {
          progressElement.textContent = `${Math.round(current / total * 100)}%`;
      };
  }

  // Load and initialize
  const loadStatus = await dopple.load()

  // Hide loading screen after Dopple loads
  const stickyImg = document.querySelector('.sticky_img');
  if (stickyImg) {
      stickyImg.classList.add('dopple_loaded');
  }

  const mwApoPrice = document.querySelector('.mw-apo-price-custom');
  if (mwApoPrice) {
      mwApoPrice.classList.add('dopple_loaded');
  }

  const loadingBg = document.querySelector('.loading_bg');
  if (loadingBg) {
      loadingBg.classList.add('dopple_loaded');
  }

  // Stop auto-rotation on first pointer interaction.
  // Previously used transitionstart on dopple.controls, which fires on every
  // auto-rotation frame → 60 queued updateSelection promises/sec → stutter.
  // pointerdown fires once, cleanly, before the drag begins.
  document.querySelector('#dopple-container')
      ?.addEventListener('pointerdown', () => {
          dopple.updateSelection({ 'autoRotation': 'off' });
      }, { passive: true, once: true });

  dopple.run();

  setTimeout(() => {
        console.log('💰 Showing price after Dopple load');
        document.querySelectorAll('.loading_price').forEach(el => el.setAttribute('style', 'opacity:0'));
        document.querySelectorAll('.price-item--regular').forEach(el => el.setAttribute('style', 'opacity:1'));
    }, 1500);
})();